import { AsyncForm, FormGroup } from '@components/async-form';
import { Button } from '@components/buttons';
import { DialogFooter, DialogHeader, StandardDialog } from '@components/dialog';
import { showModalForm } from '@components/modal-form';
import { IcoFolder } from '@components/icons';
import { SearchBox } from '@components/search-box';
import { useMemo, useState } from 'preact/hooks';
import { ManagedFolder } from './state';

function UploadDestinationDialog({
  folders,
  initialFolderId,
  onCancel,
  onSubmit,
}: {
  folders: ManagedFolder[];
  initialFolderId?: string;
  onCancel(): void;
  onSubmit(folderId: string): void;
}) {
  const [folderId, setFolderId] = useState(initialFolderId ?? '');
  return (
    <StandardDialog onClose={onCancel}>
      <form
        class="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(folderId || 'root');
        }}
      >
        <DialogHeader title="Upload files" />
        <label class="flex flex-col gap-2 text-sm font-medium text-gray-800">
          Destination folder
          <select
            autoFocus
            class="ruz-input"
            value={folderId}
            onChange={(event) => setFolderId(event.currentTarget.value)}
          >
            <option value="">Unfiled</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </select>
        </label>
        <DialogFooter onClose={onCancel} confirmButtonText="Choose files" />
      </form>
    </StandardDialog>
  );
}

export function promptUploadDestination(
  folders: ManagedFolder[],
  initialFolderId?: string,
): Promise<string | undefined> {
  return showModalForm<string | undefined>(({ resolve }) => (
    <UploadDestinationDialog
      folders={folders}
      initialFolderId={initialFolderId}
      onCancel={() => resolve(undefined)}
      onSubmit={resolve}
    />
  ));
}

type RenameProps = {
  initialName: string;
  title: string;
  confirmText: string;
  onCancel(): void;
  onSubmit(name: string): Promise<void>;
};

function NameDialog({ initialName, title, confirmText, onCancel, onSubmit }: RenameProps) {
  const [isSaving, setIsSaving] = useState(false);

  return (
    <StandardDialog onClose={onCancel}>
      <AsyncForm
        class="flex flex-col gap-4"
        onSubmit={async ({ name }: { name: string }) => {
          const trimmed = name.trim();
          if (!trimmed) {
            onCancel();
            return;
          }
          setIsSaving(true);
          try {
            await onSubmit(trimmed);
          } finally {
            setIsSaving(false);
          }
        }}
      >
        <DialogHeader title={title} />
        <FormGroup prop="name">
          <input
            type="text"
            name="name"
            class="ruz-input"
            defaultValue={initialName}
            placeholder="Folder name"
            autoFocus
            maxLength={255}
          />
        </FormGroup>
        <DialogFooter onClose={onCancel} isLoading={isSaving} confirmButtonText={confirmText} />
      </AsyncForm>
    </StandardDialog>
  );
}

export async function promptNewFolder(): Promise<string | undefined> {
  return showModalForm<string | undefined>(({ resolve }) => (
    <NameDialog
      initialName=""
      title="Create folder"
      confirmText="Create"
      onCancel={() => resolve(undefined)}
      onSubmit={async (name) => resolve(name)}
    />
  ));
}

export async function promptRenameFolder(initialName: string): Promise<string | undefined> {
  return showModalForm<string | undefined>(({ resolve }) => (
    <NameDialog
      initialName={initialName}
      title="Rename folder"
      confirmText="Rename"
      onCancel={() => resolve(undefined)}
      onSubmit={async (name) => resolve(name)}
    />
  ));
}

type MovePickerProps = {
  folders: ManagedFolder[];
  currentFolderId?: string;
  onCancel(): void;
  onSubmit(folderId: string): Promise<void>;
};

function MovePickerDialog({ folders, currentFolderId, onCancel, onSubmit }: MovePickerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const matches = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return folders;
    }
    return folders.filter((f) => f.name.toLowerCase().includes(term));
  }, [folders, searchTerm]);

  return (
    <StandardDialog onClose={onCancel}>
      <AsyncForm
        class="flex flex-col gap-4"
        onSubmit={async () => {
          if (selected === undefined) {
            return;
          }
          setIsSaving(true);
          try {
            await onSubmit(selected);
          } finally {
            setIsSaving(false);
          }
        }}
      >
        <DialogHeader title="Move to folder" />
        <SearchBox
          containerClass="w-full"
          class="ruz-input text-sm pl-9"
          placeholder="Search folders"
          value={searchTerm}
          onTermChange={setSearchTerm}
        />
        <div class="border rounded-md max-h-80 overflow-y-auto">
          <FolderOption
            label="Unfiled"
            isCurrent={currentFolderId === 'root'}
            isSelected={selected === 'root'}
            onClick={() => setSelected('root')}
          />
          {matches.map((f) => (
            <FolderOption
              key={f.id}
              label={f.name}
              isCurrent={currentFolderId === f.id}
              isSelected={selected === f.id}
              onClick={() => setSelected(f.id)}
            />
          ))}
          {matches.length === 0 && searchTerm && (
            <div class="p-3 text-sm text-gray-500 text-center">No folders match.</div>
          )}
        </div>
        <DialogFooter
          onClose={onCancel}
          isLoading={isSaving}
          confirmButtonText="Move"
          isConfirmDisabled={selected === undefined}
        />
      </AsyncForm>
    </StandardDialog>
  );
}

function FolderOption({
  label,
  isCurrent,
  isSelected,
  onClick,
}: {
  label: string;
  isCurrent: boolean;
  isSelected: boolean;
  onClick(): void;
}) {
  return (
    <Button
      onClick={onClick}
      disabled={isCurrent}
      class={`w-full flex items-center gap-2 px-3 py-2 text-left border-b last:border-b-0 ${
        isSelected
          ? 'bg-indigo-600 text-white'
          : isCurrent
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-gray-50'
      }`}
    >
      <IcoFolder class="size-4 shrink-0" />
      <span class="truncate">{label}</span>
      {isCurrent && <span class="ml-auto text-xs">current</span>}
    </Button>
  );
}

export async function promptMoveToFolder(opts: {
  folders: ManagedFolder[];
  currentFolderId?: string;
}): Promise<string | undefined> {
  return showModalForm<string | undefined>(({ resolve }) => (
    <MovePickerDialog
      folders={opts.folders}
      currentFolderId={opts.currentFolderId}
      onCancel={() => resolve(undefined)}
      onSubmit={async (folderId) => resolve(folderId)}
    />
  ));
}
